import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { SendNotificationDialog } from '@/components/issues/SendNotificationDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import type { ReportedIssue } from '@/types';

const statusColors = {
  new: 'bg-blue-50 text-blue-700 ring-blue-700/10',
  'in review': 'bg-yellow-50 text-yellow-700 ring-yellow-700/10',
  'on hold': 'bg-orange-50 text-orange-700 ring-orange-700/10',
  resolved: 'bg-green-50 text-green-700 ring-green-700/10',
  rejected: 'bg-red-50 text-red-700 ring-red-700/10',
} as const;

export default function ViewIssue() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [issue, setIssue] = useState<ReportedIssue | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [lastNotification, setLastNotification] = useState<string | null>(null);

  const fetchNotificationCount = async (issueId: string) => {
    try {
      const { data, error } = await supabase
        .from('sms_notification_logs')
        .select('created_at')
        .eq('issue_id', issueId)
        .eq('status', 'sent')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setNotificationCount(data?.length || 0);
      if (data?.[0]?.created_at) {
        setLastNotification(data[0].created_at);
      }
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  useEffect(() => {
    fetchIssue();
    if (id) {
      fetchNotificationCount(id);
    }
  }, [id]);

  const handleSendNotifications = () => {
    setNotificationDialogOpen(true);
  };

  const handleNotificationComplete = () => {
    setNotificationDialogOpen(false);
    if (id) {
      fetchNotificationCount(id);
    }
  };

  const fetchIssue = async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from('sms_reported_issues')
        .select(`
          *,
          school:sms_schools(name),
          grade:sms_grades(name)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!data) {
        toast.error('Issue not found');
        navigate('/reported-issues');
        return;
      }

      setIssue(data);
    } catch (error) {
      console.error('Error fetching issue:', error);
      toast.error('Failed to fetch issue');
      navigate('/reported-issues');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading || !issue) {
    return <div>Loading...</div>;
  }

  return (
    <div className="container max-w-2xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Issue #{issue.issue_number}</h1>
          <Badge
            variant="outline"
            className={`${statusColors[issue.status as keyof typeof statusColors]}`}
          >
            {issue.status}
          </Badge>
        </div>
        <p className="text-muted-foreground mt-2">
          Reported on {formatDate(issue.created_at!)}
        </p>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>School Information</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 gap-4">
              <div>
                <dt className="text-sm font-medium text-muted-foreground">School</dt>
                <dd className="mt-1 text-sm">{issue.school?.name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Grade</dt>
                <dd className="mt-1 text-sm">{issue.grade?.name}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Teacher Information</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 gap-4">
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Teacher Name</dt>
                <dd className="mt-1 text-sm">{issue.teacher_name}</dd>
              </div>
              {issue.contact_number && (
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Contact Number</dt>
                  <dd className="mt-1 text-sm">{issue.contact_number}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Subject Not Being Taught</dt>
                <dd className="mt-1 text-sm">{issue.subject}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Issue Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 gap-4">
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Details of Concern</dt>
                <dd className="mt-1 text-sm whitespace-pre-wrap">{issue.details}</dd>
              </div>
              {issue.remarks && (
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Remarks</dt>
                  <dd className="mt-1 text-sm whitespace-pre-wrap">{issue.remarks}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => navigate('/admin/reported-issues')}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to List
          </Button>
          <Button
            variant="outline"
            onClick={handleSendNotifications}
            className="flex items-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
            title={lastNotification ? `Last sent: ${formatDate(lastNotification)}` : undefined}
          >
            <Mail className="h-4 w-4" />
            Send Notifications
            {notificationCount > 0 && (
              <Badge className="ml-1 bg-blue-100 text-blue-700" title={`Sent ${notificationCount} time${notificationCount !== 1 ? 's' : ''}`}>
                {notificationCount}
              </Badge>
            )}
          </Button>
          <Button
            className="gap-2"
            onClick={() => navigate(`/admin/reported-issues/${id}/edit`)}
          >
            Edit Issue
          </Button>
        </div>

        <SendNotificationDialog
          issue={issue}
          open={notificationDialogOpen}
          onClose={handleNotificationComplete}
        />
      </div>
    </div>
  );
}